import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Tab,
    Table,
    Badge,
    Button,
    Form,
    Alert,
    Spinner,
    Modal,
} from 'react-bootstrap';
import termService from '@/shared/services/termService';
import programService from '@/shared/services/programService';
import budgetService from '@/shared/services/budgetService';
import DashboardNav, {
    generateChildId,
} from '@/components/common/DashboardNav';
import BudgetCards from '@/features/admin/BudgetCards';
import UsersTab from '@/features/admin/UsersTab';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import EmailPreviewsTab from '@/features/admin/EmailPreviewsTab';
import PartsOfTermEditor, {
    emptyPartOfTerm,
} from '@/features/admin/PartsOfTermEditor';
import StudentEmploymentRulesCard from '@/features/admin/StudentEmploymentRulesCard';
import TutoringPageCard from '@/features/admin/TutoringPageCard';
import ProgramNotificationSettings from '@/features/programs/ProgramNotificationSettings';
import { PROGRAM_DISPLAY_ORDER } from '@/shared/utils/programOrder';
import {
    DEFAULT_PART_OF_TERM,
    partsOfTermOf,
} from '@/shared/utils/partsOfTerm';
import './AdminPage.css';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { formatCalendarDate, formatDeadlineDay } from '@/shared/utils/deadline';

const PROGRAM_CONFIG = {
    si: { label: 'SI', full: 'Supplemental Instruction' },
    ta: { label: 'TA', full: 'Teaching Assistants' },
    la: { label: 'LA', full: 'Learning Assistants' },
};

const ADMIN_TABS = [
    { key: 'terms', label: 'Terms & Semesters' },
    { key: 'programs', label: 'Program Config' },
    { key: 'budgets', label: 'Budgets' },
    { key: 'users', label: 'User Management' },
    { key: 'email', label: 'Email Previews' },
    { key: 'settings', label: 'System Settings' },
];

function AdminPage() {
    const phone = useMediaQuery('(max-width: 767.98px)');
    const [searchParams] = useSearchParams();
    // A link may open one tab directly (?tab=budgets from a coordinator's
    // "No budget set")
    const [activeTab, setActiveTab] = useState(() =>
        ADMIN_TABS.some((tab) => tab.key === searchParams.get('tab'))
            ? searchParams.get('tab')
            : 'terms'
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Term management state
    const [terms, setTerms] = useState([]);
    const [showTermModal, setShowTermModal] = useState(false);
    const [editingTerm, setEditingTerm] = useState(null);

    // Program config state
    const [programConfigs, setProgramConfigs] = useState([]);
    const [showProgramModal, setShowProgramModal] = useState(false);
    const [notificationProgram, setNotificationProgram] = useState(null);
    const [editingProgram, setEditingProgram] = useState(null);

    // Budget management state
    const [budgetSummaries, setBudgetSummaries] = useState([]);
    const [budgetTotals, setBudgetTotals] = useState(null);
    const [selectedBudgetTerm, setSelectedBudgetTerm] = useState('');
    const [showBudgetModal, setShowBudgetModal] = useState(false);
    const [editingBudget, setEditingBudget] = useState(null);

    useEffect(() => {
        loadData();
    }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);

            if (activeTab === 'terms') {
                const data = await termService.getAllTerms();
                setTerms(data);
            } else if (activeTab === 'programs') {
                const data = await programService.getAllConfigs();
                setProgramConfigs(data);
            } else if (activeTab === 'budgets') {
                // Load terms so we can pick one for budgets
                const termData = await termService.getAllTerms();
                setTerms(termData);
                // If we have a selected term, load budgets for it
                if (selectedBudgetTerm) {
                    const { summaries, totals } =
                        await budgetService.getSummaryByTerm(
                            selectedBudgetTerm
                        );
                    setBudgetSummaries(summaries || []);
                    setBudgetTotals(totals || null);
                } else if (termData.length > 0) {
                    // Auto-select the first active term, or just the first term
                    const activeTerm =
                        termData.find((t) => t.isActive) || termData[0];
                    setSelectedBudgetTerm(activeTerm.code);
                    const { summaries, totals } =
                        await budgetService.getSummaryByTerm(activeTerm.code);
                    setBudgetSummaries(summaries || []);
                    setBudgetTotals(totals || null);
                }
            }
        } catch (_err) {
            setError('Failed to load data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleTermActive = async (termCode, isActive) => {
        try {
            await termService.updateTerm(termCode, { isActive: !isActive });
            const data = await termService.getAllTerms();
            setTerms(data);
        } catch (_err) {
            setError('Failed to update term.');
        }
    };

    // Returns the reason a save failed, for the form to show beside its own
    // fields (a program window whose close is not after its open, say), or
    // null on success.
    const handleSaveTerm = async (termData) => {
        try {
            if (editingTerm) {
                await termService.updateTerm(editingTerm.code, termData);
            } else {
                await termService.createTerm(termData);
            }
            setShowTermModal(false);
            setEditingTerm(null);
            const data = await termService.getAllTerms();
            setTerms(data);
            return null;
        } catch (err) {
            return (
                err.response?.data?.message ||
                `Failed to ${editingTerm ? 'update' : 'create'} term.`
            );
        }
    };

    const handleOpenProgramConfig = (type) => {
        const config = programConfigs.find((c) => c.type === type);
        setEditingProgram({ type, config });
        setShowProgramModal(true);
    };

    // Returns the reason a save failed, for the modal to show beside its
    // fields (a malformed course prefix, say), or null on success.
    const handleSaveProgramConfig = async (configData) => {
        try {
            await programService.upsertConfig(editingProgram.type, configData);
            setShowProgramModal(false);
            setEditingProgram(null);
            const data = await programService.getAllConfigs();
            setProgramConfigs(data);
            return null;
        } catch (err) {
            return (
                err.response?.data?.message ||
                'Failed to save program configuration.'
            );
        }
    };

    // The contact address shown on the program card may have changed
    const handleCloseNotifications = async () => {
        setNotificationProgram(null);
        try {
            setProgramConfigs(await programService.getAllConfigs());
        } catch (_err) {
            // The card keeps showing the last loaded configuration
        }
    };

    const loadBudgets = async (termCode) => {
        try {
            setLoading(true);
            const { summaries, totals } =
                await budgetService.getSummaryByTerm(termCode);
            setBudgetSummaries(summaries || []);
            setBudgetTotals(totals || null);
        } catch (_err) {
            setError('Failed to load budget data.');
        } finally {
            setLoading(false);
        }
    };

    const handleBudgetTermChange = (termCode) => {
        setSelectedBudgetTerm(termCode);
        if (termCode) {
            loadBudgets(termCode);
        } else {
            setBudgetSummaries([]);
            setBudgetTotals(null);
        }
    };

    const handleSaveBudget = async (budgetData) => {
        try {
            if (editingBudget) {
                await budgetService.updateBudget(
                    editingBudget.budget._id,
                    budgetData
                );
            } else {
                await budgetService.createBudget(budgetData);
            }
            setShowBudgetModal(false);
            setEditingBudget(null);
            if (selectedBudgetTerm) {
                await loadBudgets(selectedBudgetTerm);
            }
        } catch (_err) {
            setError(
                `Failed to ${editingBudget ? 'update' : 'create'} budget.`
            );
        }
    };

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    // Term dates are calendar days stored at midnight UTC; the shared helper
    // shows the day that was entered, not the day before in Mountain time.
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return formatDeadlineDay(dateString);
    };

    return (
        <Container className="py-4">
            <div className="mb-4">
                <h1>System Administration</h1>
                <p className="text-muted">
                    Manage terms, programs, users, and system settings.
                </p>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            <Tab.Container
                activeKey={activeTab}
                generateChildId={generateChildId}
            >
                <DashboardNav
                    ariaLabel="Admin sections"
                    groups={ADMIN_TABS.map((tab) => ({
                        key: tab.key,
                        label: tab.label,
                        tabs: [tab],
                    }))}
                    activeTab={activeTab}
                    onSelect={setActiveTab}
                />
                <Tab.Content>
                    {/* Terms Tab */}
                    <Tab.Pane eventKey="terms">
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-transparent d-flex justify-content-between align-items-center">
                                <h2 className="h5 mb-0">Term Management</h2>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => {
                                        setEditingTerm(null);
                                        setShowTermModal(true);
                                    }}
                                >
                                    + New Term
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {loading ? (
                                    <div className="text-center py-4">
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            role="status"
                                        >
                                            <span className="visually-hidden">
                                                Loading terms...
                                            </span>
                                        </Spinner>
                                    </div>
                                ) : terms.length === 0 ? (
                                    <p className="text-muted text-center">
                                        No terms configured. Create one to get
                                        started.
                                    </p>
                                ) : (
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>Code</th>
                                                <th>Name</th>
                                                <th>Dates</th>
                                                <th>Applications</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {terms.map((term) => (
                                                <tr key={term.code}>
                                                    <td>
                                                        <code>{term.code}</code>
                                                    </td>
                                                    <td>{term.name}</td>
                                                    <td className="small">
                                                        {formatDate(
                                                            term.startDate
                                                        )}{' '}
                                                        -{' '}
                                                        {formatDate(
                                                            term.endDate
                                                        )}
                                                    </td>
                                                    <td className="small">
                                                        {term.applicationsOpen ? (
                                                            <Badge bg="success">
                                                                Open
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="secondary">
                                                                Closed
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {term.isActive ? (
                                                            <Badge bg="primary">
                                                                Active
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                bg="light"
                                                                text="dark"
                                                            >
                                                                Inactive
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            className="me-1"
                                                            onClick={() => {
                                                                setEditingTerm(
                                                                    term
                                                                );
                                                                setShowTermModal(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            variant={
                                                                term.isActive
                                                                    ? 'outline-warning'
                                                                    : 'outline-success'
                                                            }
                                                            size="sm"
                                                            onClick={() =>
                                                                handleToggleTermActive(
                                                                    term.code,
                                                                    term.isActive
                                                                )
                                                            }
                                                        >
                                                            {term.isActive
                                                                ? 'Deactivate'
                                                                : 'Activate'}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    {/* Programs Tab */}
                    <Tab.Pane eventKey="programs">
                        <Row>
                            {PROGRAM_DISPLAY_ORDER.map((type) => {
                                const config = programConfigs.find(
                                    (c) => c.type === type
                                );
                                const info = PROGRAM_CONFIG[type];
                                return (
                                    <Col md={12} key={type} className="mb-3">
                                        <Card
                                            className="admin-program-card border-0 shadow-sm"
                                            data-program={type}
                                        >
                                            <Card.Body>
                                                <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-start">
                                                    <div>
                                                        <h2 className="h5 mb-1">
                                                            {config?.displayName ||
                                                                info.full}
                                                            <Badge
                                                                className="ms-2"
                                                                bg={
                                                                    config?.isActive !==
                                                                    false
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                }
                                                                text={badgeTextFor(
                                                                    config?.isActive !==
                                                                        false
                                                                        ? 'success'
                                                                        : 'secondary'
                                                                )}
                                                            >
                                                                {config?.isActive !==
                                                                false
                                                                    ? 'Active'
                                                                    : 'Inactive'}
                                                            </Badge>
                                                        </h2>
                                                        <p className="text-muted small mb-2">
                                                            {config?.description ||
                                                                'No description configured.'}
                                                        </p>
                                                        {config
                                                            ?.allowedSubjectPrefixes
                                                            ?.length > 0 && (
                                                            <span className="small text-muted me-3">
                                                                Course prefixes:{' '}
                                                                {config.allowedSubjectPrefixes.join(
                                                                    ', '
                                                                )}
                                                            </span>
                                                        )}
                                                        {config?.contactEmail && (
                                                            <span className="small text-muted">
                                                                Contact:{' '}
                                                                {
                                                                    config.contactEmail
                                                                }
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="psh-button-pair flex-shrink-0">
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleOpenProgramConfig(
                                                                    type
                                                                )
                                                            }
                                                        >
                                                            Configure
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() =>
                                                                setNotificationProgram(
                                                                    type
                                                                )
                                                            }
                                                        >
                                                            Notifications
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    </Tab.Pane>

                    {/* Budgets Tab */}
                    <Tab.Pane eventKey="budgets">
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-transparent d-flex flex-wrap justify-content-between align-items-center gap-2">
                                <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3">
                                    <h2 className="h5 mb-0">
                                        Budget Management
                                    </h2>
                                    <Form.Select
                                        size="sm"
                                        className="admin-budget-term-select"
                                        aria-label="Select term for budget view"
                                        value={selectedBudgetTerm}
                                        onChange={(e) =>
                                            handleBudgetTermChange(
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">Select term...</option>
                                        {terms.map((t) => (
                                            <option key={t.code} value={t.code}>
                                                {t.name} ({t.code})
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    disabled={!selectedBudgetTerm}
                                    onClick={() => {
                                        setEditingBudget(null);
                                        setShowBudgetModal(true);
                                    }}
                                >
                                    + Create Budget
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {!selectedBudgetTerm ? (
                                    <p className="text-muted text-center py-4">
                                        Select a term to view budget
                                        allocations.
                                    </p>
                                ) : loading ? (
                                    <div className="text-center py-4">
                                        <Spinner
                                            animation="border"
                                            size="sm"
                                            role="status"
                                        >
                                            <span className="visually-hidden">
                                                Loading budgets...
                                            </span>
                                        </Spinner>
                                    </div>
                                ) : budgetSummaries.length === 0 ? (
                                    <p className="text-muted text-center py-4">
                                        No budgets configured for this term.
                                        Create one to get started.
                                    </p>
                                ) : phone ? (
                                    <BudgetCards
                                        summaries={budgetSummaries}
                                        totals={budgetTotals}
                                        programs={PROGRAM_CONFIG}
                                        formatCurrency={formatCurrency}
                                        onEdit={(summary) => {
                                            setEditingBudget(summary);
                                            setShowBudgetModal(true);
                                        }}
                                    />
                                ) : (
                                    <Table responsive hover size="sm">
                                        <thead>
                                            <tr>
                                                <th>Program</th>
                                                <th className="text-end">
                                                    Allocated Positions
                                                </th>
                                                <th className="text-end">
                                                    Pay Rate
                                                </th>
                                                <th className="text-end">
                                                    Hrs/Position
                                                </th>
                                                <th className="text-end">
                                                    Allocated Amount
                                                </th>
                                                <th className="text-end">
                                                    Offered
                                                </th>
                                                <th className="text-end">
                                                    Accepted
                                                </th>
                                                <th className="text-end">
                                                    Remaining
                                                </th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {budgetSummaries.map((summary) => {
                                                const info =
                                                    PROGRAM_CONFIG[
                                                        summary.budget.program
                                                    ] || {};
                                                return (
                                                    <tr
                                                        key={summary.budget._id}
                                                    >
                                                        <td>
                                                            <Badge
                                                                className="admin-program-badge"
                                                                data-program={
                                                                    summary
                                                                        .budget
                                                                        .program
                                                                }
                                                            >
                                                                {info.label ||
                                                                    summary.budget.program?.toUpperCase()}
                                                            </Badge>{' '}
                                                            {info.full}
                                                        </td>
                                                        <td className="text-end">
                                                            {summary.budget
                                                                .allocatedPositions ??
                                                                '-'}
                                                        </td>
                                                        <td className="text-end">
                                                            {summary.budget
                                                                .payRate
                                                                ? `$${summary.budget.payRate}/hr`
                                                                : '-'}
                                                        </td>
                                                        <td className="text-end">
                                                            {summary.budget
                                                                .hoursPerPosition ??
                                                                '-'}
                                                        </td>
                                                        <td className="text-end">
                                                            {formatCurrency(
                                                                summary.budget
                                                                    .allocatedAmount
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                            {summary.offeredPositions ??
                                                                0}
                                                        </td>
                                                        <td className="text-end">
                                                            {summary.acceptedPositions ??
                                                                0}
                                                        </td>
                                                        <td className="text-end">
                                                            {summary.remainingPositions ??
                                                                '-'}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => {
                                                                    setEditingBudget(
                                                                        summary
                                                                    );
                                                                    setShowBudgetModal(
                                                                        true
                                                                    );
                                                                }}
                                                            >
                                                                Edit
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                        {budgetTotals && (
                                            <tfoot>
                                                <tr className="fw-bold table-light">
                                                    <td>Totals</td>
                                                    <td className="text-end">
                                                        {budgetTotals.allocatedPositions ??
                                                            '-'}
                                                    </td>
                                                    <td className="text-end">
                                                        -
                                                    </td>
                                                    <td className="text-end">
                                                        -
                                                    </td>
                                                    <td className="text-end">
                                                        {formatCurrency(
                                                            budgetTotals.allocatedAmount
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        {budgetTotals.offeredPositions ??
                                                            0}
                                                    </td>
                                                    <td className="text-end">
                                                        {budgetTotals.acceptedPositions ??
                                                            0}
                                                    </td>
                                                    <td className="text-end">
                                                        {budgetTotals.remainingPositions ??
                                                            '-'}
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>

                    {/* Users Tab */}
                    <Tab.Pane eventKey="users" mountOnEnter>
                        <UsersTab />
                    </Tab.Pane>

                    {/* Settings Tab */}
                    <Tab.Pane eventKey="email" mountOnEnter unmountOnExit>
                        <EmailPreviewsTab />
                    </Tab.Pane>

                    <Tab.Pane eventKey="settings" mountOnEnter>
                        <StudentEmploymentRulesCard />
                        <TutoringPageCard />
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="text-center py-5">
                                <h2 className="h5 text-muted">
                                    System Settings
                                </h2>
                                <p className="text-muted">
                                    Feature flags, notification preferences, and
                                    integration settings will be configured
                                    here.
                                </p>
                                <p className="small text-muted">
                                    Course data sync (Banner/Workday), email
                                    notification templates, and system-wide
                                    defaults.
                                </p>
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            {/* Term Create/Edit Modal */}
            <TermModal
                show={showTermModal}
                onHide={() => {
                    setShowTermModal(false);
                    setEditingTerm(null);
                }}
                onSave={handleSaveTerm}
                term={editingTerm}
            />

            {/* Program Config Modal */}
            <ProgramConfigModal
                show={showProgramModal}
                onHide={() => {
                    setShowProgramModal(false);
                    setEditingProgram(null);
                }}
                onSave={handleSaveProgramConfig}
                programType={editingProgram?.type}
                config={editingProgram?.config}
            />

            {/* Program Notification Settings Modal */}
            <Modal
                show={Boolean(notificationProgram)}
                onHide={handleCloseNotifications}
                size="xl"
                centered
                aria-labelledby="admin-notification-modal-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title
                        id="admin-notification-modal-title"
                        as="h2"
                        className="h5"
                    >
                        {notificationProgram?.toUpperCase()} notifications
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {notificationProgram && (
                        <ProgramNotificationSettings
                            programType={notificationProgram}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {/* Budget Create/Edit Modal */}
            <BudgetModal
                show={showBudgetModal}
                onHide={() => {
                    setShowBudgetModal(false);
                    setEditingBudget(null);
                }}
                onSave={handleSaveBudget}
                budget={editingBudget}
                terms={terms}
                selectedTerm={selectedBudgetTerm}
            />
        </Container>
    );
}

const TERM_PROGRAMS = PROGRAM_DISPLAY_ORDER;

const toDateInput = (val) => (val ? val.split('T')[0] : '');

// Only the application dates: the review dates belong to each program's
// coordinator, and the server merges programWindows field by field, so
// sending these cannot disturb them.
function programApplicationInputs(term) {
    const inputs = {};
    for (const prog of TERM_PROGRAMS) {
        const w = term?.programWindows?.[prog];
        inputs[prog] = {
            applicationOpenDate: toDateInput(w?.applicationOpenDate),
            applicationCloseDate: toDateInput(w?.applicationCloseDate),
            studentApplicationCloseDate: toDateInput(
                w?.studentApplicationCloseDate
            ),
        };
    }
    return inputs;
}

// The dialog's rows for a term's parts of term; a new term starts with the
// full semester
function partsOfTermInputs(term) {
    if (!term) return [{ ...emptyPartOfTerm(), ...DEFAULT_PART_OF_TERM }];
    return partsOfTermOf(term).map((part) => ({
        code: part.code,
        name: part.name,
        startDate: toDateInput(part.startDate),
        endDate: toDateInput(part.endDate),
    }));
}

function TermModal({ show, onHide, onSave, term }) {
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        startDate: '',
        endDate: '',
        applicationOpenDate: '',
        applicationCloseDate: '',
        programWindows: programApplicationInputs(null),
        partsOfTerm: partsOfTermInputs(null),
        trainingDeadline: '',
        onboardingStart: '',
        onboardingEnd: '',
        applicationsOpen: false,
        isActive: false,
    });
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        setSaveError(null);
        if (term) {
            setFormData({
                code: term.code || '',
                name: term.name || '',
                startDate: toDateInput(term.startDate),
                endDate: toDateInput(term.endDate),
                applicationOpenDate: toDateInput(term.applicationOpenDate),
                applicationCloseDate: toDateInput(term.applicationCloseDate),
                programWindows: programApplicationInputs(term),
                partsOfTerm: partsOfTermInputs(term),
                trainingDeadline: toDateInput(term.trainingDeadline),
                onboardingStart: toDateInput(term.onboardingStart),
                onboardingEnd: toDateInput(term.onboardingEnd),
                applicationsOpen: term.applicationsOpen || false,
                isActive: term.isActive || false,
            });
        } else {
            setFormData({
                code: '',
                name: '',
                startDate: '',
                endDate: '',
                applicationOpenDate: '',
                applicationCloseDate: '',
                programWindows: programApplicationInputs(null),
                partsOfTerm: partsOfTermInputs(null),
                trainingDeadline: '',
                onboardingStart: '',
                onboardingEnd: '',
                applicationsOpen: false,
                isActive: false,
            });
        }
    }, [term, show]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleProgramDateChange = (prog, field, value) => {
        setFormData((prev) => ({
            ...prev,
            programWindows: {
                ...prev.programWindows,
                [prog]: { ...prev.programWindows[prog], [field]: value },
            },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaveError(await onSave(formData));
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            aria-labelledby="admin-modal-title-1"
        >
            <Modal.Header closeButton>
                <Modal.Title id="admin-modal-title-1">
                    {term ? 'Edit Term' : 'New Term'}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {saveError && (
                        <Alert variant="danger" role="alert">
                            {saveError}
                        </Alert>
                    )}
                    <h6 className="text-muted mb-3">Basic Information</h6>
                    <Row>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-term-code"
                            >
                                <Form.Label>Term Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., 202710"
                                    value={formData.code}
                                    onChange={(e) =>
                                        handleChange('code', e.target.value)
                                    }
                                    disabled={!!term}
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={8}>
                            <Form.Group className="mb-3" controlId="admin-name">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., Spring 2027"
                                    value={formData.name}
                                    onChange={(e) =>
                                        handleChange('name', e.target.value)
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-term-start"
                            >
                                <Form.Label>Term Start</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.startDate}
                                    onChange={(e) =>
                                        handleChange(
                                            'startDate',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-term-end"
                            >
                                <Form.Label>Term End</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.endDate}
                                    onChange={(e) =>
                                        handleChange('endDate', e.target.value)
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />
                    <h6 className="text-muted mb-1">Application Window</h6>
                    <p className="small text-muted mb-3">
                        The default for every program. A program with its own
                        dates below uses those instead.
                    </p>
                    <Row>
                        <Col>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-applications-open-date"
                            >
                                <Form.Label>Applications Open Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.applicationOpenDate}
                                    onChange={(e) =>
                                        handleChange(
                                            'applicationOpenDate',
                                            e.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-applications-close-date"
                            >
                                <Form.Label>Applications Close Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.applicationCloseDate}
                                    onChange={(e) =>
                                        handleChange(
                                            'applicationCloseDate',
                                            e.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Check
                        type="switch"
                        className="mb-3"
                        label="Applications currently accepting submissions"
                        checked={formData.applicationsOpen}
                        onChange={(e) =>
                            handleChange('applicationsOpen', e.target.checked)
                        }
                    />

                    <hr />
                    <h6 className="text-muted mb-3">Training & Onboarding</h6>
                    <Row>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-training-deadline"
                            >
                                <Form.Label>Training Deadline</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.trainingDeadline}
                                    onChange={(e) =>
                                        handleChange(
                                            'trainingDeadline',
                                            e.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-onboarding-start"
                            >
                                <Form.Label>Onboarding Start</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.onboardingStart}
                                    onChange={(e) =>
                                        handleChange(
                                            'onboardingStart',
                                            e.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-onboarding-end"
                            >
                                <Form.Label>Onboarding End</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={formData.onboardingEnd}
                                    onChange={(e) =>
                                        handleChange(
                                            'onboardingEnd',
                                            e.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />
                    <h6 className="text-muted mb-1">Program Windows</h6>
                    <p className="small text-muted mb-3">
                        Leave a program&apos;s faculty application dates blank
                        to use the term&apos;s. The student close date never
                        uses the term&apos;s: blank means students have no close
                        date. Review and decision dates are set by each
                        program&apos;s coordinator.
                    </p>
                    <Row>
                        {TERM_PROGRAMS.map((prog) => {
                            const w = term?.programWindows?.[prog];
                            const label = prog.toUpperCase();
                            return (
                                <Col md={4} key={prog}>
                                    <Card className="mb-3 border">
                                        <Card.Body className="py-2 px-3">
                                            <strong className="d-block small mb-2">
                                                {label}
                                            </strong>
                                            <Form.Group
                                                className="mb-2"
                                                controlId={`admin-${prog}-applications-open`}
                                            >
                                                <Form.Label className="small mb-1">
                                                    {label} applications open
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={
                                                        formData.programWindows[
                                                            prog
                                                        ].applicationOpenDate
                                                    }
                                                    onChange={(e) =>
                                                        handleProgramDateChange(
                                                            prog,
                                                            'applicationOpenDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Form.Group>
                                            <Form.Group
                                                className="mb-2"
                                                controlId={`admin-${prog}-applications-close`}
                                            >
                                                <Form.Label className="small mb-1">
                                                    {label} applications close
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={
                                                        formData.programWindows[
                                                            prog
                                                        ].applicationCloseDate
                                                    }
                                                    onChange={(e) =>
                                                        handleProgramDateChange(
                                                            prog,
                                                            'applicationCloseDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Form.Group>
                                            <Form.Group
                                                className="mb-2"
                                                controlId={`admin-${prog}-student-applications-close`}
                                            >
                                                <Form.Label className="small mb-1">
                                                    {label} student applications
                                                    close
                                                </Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    size="sm"
                                                    value={
                                                        formData.programWindows[
                                                            prog
                                                        ]
                                                            .studentApplicationCloseDate
                                                    }
                                                    onChange={(e) =>
                                                        handleProgramDateChange(
                                                            prog,
                                                            'studentApplicationCloseDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </Form.Group>
                                            <div className="small text-muted">
                                                Review:{' '}
                                                {w?.reviewStart
                                                    ? formatCalendarDate(
                                                          w.reviewStart
                                                      )
                                                    : 'Not set'}{' '}
                                                &ndash;{' '}
                                                {w?.reviewEnd
                                                    ? formatCalendarDate(
                                                          w.reviewEnd
                                                      )
                                                    : 'Not set'}
                                            </div>
                                            <div className="small text-muted">
                                                Decisions:{' '}
                                                {w?.decisionsReleased
                                                    ? formatCalendarDate(
                                                          w.decisionsReleased
                                                      )
                                                    : 'Not set'}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>

                    <hr />
                    <PartsOfTermEditor
                        parts={formData.partsOfTerm}
                        onChange={(partsOfTerm) =>
                            handleChange('partsOfTerm', partsOfTerm)
                        }
                    />

                    <hr />
                    <Form.Check
                        type="switch"
                        label="Set as active term"
                        checked={formData.isActive}
                        onChange={(e) =>
                            handleChange('isActive', e.target.checked)
                        }
                    />
                    {formData.isActive && (
                        <p className="small text-warning mt-1 mb-0">
                            Setting this term as active will deactivate all
                            other terms.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit">
                        {term ? 'Save Changes' : 'Create Term'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

function ProgramConfigModal({ show, onHide, onSave, programType, config }) {
    const info = PROGRAM_CONFIG[programType] || {};

    const [formData, setFormData] = useState({
        displayName: '',
        description: '',
        contactName: '',
        requiresPriorCompletion: false,
        isActive: true,
        maxPositions: '',
        visibility: '',
        facultyStageControl: '',
        offerAuthority: '',
        resumeRequired: '',
        subjectPrefixes: '',
        hoursPerWeekPerPosition: '',
        maxPositionsPerStudent: '',
    });
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        setSaveError(null);
        if (config) {
            setFormData({
                displayName: config.displayName || info.full || '',
                description: config.description || '',
                contactName: config.contactName || '',
                requiresPriorCompletion:
                    config.eligibilityRules?.requiresPriorCompletion || false,
                isActive: config.isActive !== false,
                maxPositions: config.capacity?.maxPositions || '',
                visibility: config.toggles?.visibility || '',
                facultyStageControl: String(
                    config.toggles?.facultyStageControl ?? ''
                ),
                offerAuthority: String(config.toggles?.offerAuthority ?? ''),
                resumeRequired: String(config.toggles?.resumeRequired ?? ''),
                subjectPrefixes: (config.allowedSubjectPrefixes || []).join(
                    ', '
                ),
                hoursPerWeekPerPosition: config.hoursPerWeekPerPosition ?? '',
                maxPositionsPerStudent: config.maxPositionsPerStudent ?? '',
            });
        } else {
            setFormData({
                displayName: info.full || '',
                description: '',
                contactName: '',
                requiresPriorCompletion: false,
                isActive: true,
                maxPositions: '',
                visibility: '',
                facultyStageControl: '',
                offerAuthority: '',
                resumeRequired: '',
                subjectPrefixes: '',
                hoursPerWeekPerPosition: '',
                maxPositionsPerStudent: '',
            });
        }
    }, [config, show]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            displayName: formData.displayName,
            shortName: programType?.toUpperCase() || '',
            description: formData.description,
            contactName: formData.contactName,
            isActive: formData.isActive,
            eligibilityRules: {
                requiresPriorCompletion: formData.requiresPriorCompletion,
            },
            // Sent as typed; the server normalises it and refuses a
            // malformed prefix, and the message comes back through saveError.
            allowedSubjectPrefixes: formData.subjectPrefixes,
            toggles: {
                // null returns a toggle to the program's default
                visibility: formData.visibility || null,
                facultyStageControl:
                    formData.facultyStageControl === ''
                        ? null
                        : formData.facultyStageControl === 'true',
                offerAuthority: formData.offerAuthority || null,
                resumeRequired:
                    formData.resumeRequired === ''
                        ? null
                        : formData.resumeRequired === 'true',
            },
            capacity: {
                maxPositions: formData.maxPositions
                    ? parseInt(formData.maxPositions, 10)
                    : undefined,
            },
            // null clears a limit, which returns the program to its default
            hoursPerWeekPerPosition:
                formData.hoursPerWeekPerPosition === ''
                    ? null
                    : Number(formData.hoursPerWeekPerPosition),
            maxPositionsPerStudent:
                formData.maxPositionsPerStudent === ''
                    ? null
                    : Number(formData.maxPositionsPerStudent),
        };
        setSaveError(await onSave(payload));
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            aria-labelledby="admin-modal-title-2"
        >
            <Modal.Header closeButton>
                <Modal.Title id="admin-modal-title-2">
                    Configure {info.full || 'Program'} ({info.label})
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {saveError && (
                        <Alert variant="danger" role="alert">
                            {saveError}
                        </Alert>
                    )}
                    <h6 className="text-muted mb-3">Display Information</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-display-name"
                            >
                                <Form.Label>Display Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.displayName}
                                    onChange={(e) =>
                                        handleChange(
                                            'displayName',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-contact-name"
                            >
                                <Form.Label>Contact Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Program coordinator name"
                                    value={formData.contactName}
                                    onChange={(e) =>
                                        handleChange(
                                            'contactName',
                                            e.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3" controlId="admin-description">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Brief program description..."
                            value={formData.description}
                            onChange={(e) =>
                                handleChange('description', e.target.value)
                            }
                        />
                    </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">Eligibility Rules</h6>
                    <Row>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-max-positions"
                            >
                                <Form.Label>Max Positions</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    placeholder="Unlimited"
                                    value={formData.maxPositions}
                                    onChange={(e) =>
                                        handleChange(
                                            'maxPositions',
                                            e.target.value
                                        )
                                    }
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Check
                        type="switch"
                        id="admin-requires-prior-completion"
                        className="mb-3"
                        label="Requires prior course completion"
                        checked={formData.requiresPriorCompletion}
                        onChange={(e) =>
                            handleChange(
                                'requiresPriorCompletion',
                                e.target.checked
                            )
                        }
                    />
                    <Form.Group
                        className="mb-3"
                        controlId="admin-subject-prefixes"
                    >
                        <Form.Label>Course prefixes served</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Every prefix"
                            aria-describedby="admin-subject-prefixes-help"
                            value={formData.subjectPrefixes}
                            onChange={(e) =>
                                handleChange('subjectPrefixes', e.target.value)
                            }
                        />
                        <Form.Text id="admin-subject-prefixes-help">
                            Comma or space separated, like CHE, MTH. Leave empty
                            to serve courses with any prefix.
                        </Form.Text>
                    </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">Offer Settings</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-faculty-stage-control"
                            >
                                <Form.Label>
                                    Faculty may move candidates through stages
                                </Form.Label>
                                <Form.Select
                                    value={formData.facultyStageControl}
                                    onChange={(e) =>
                                        handleChange(
                                            'facultyStageControl',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="">
                                        Program default (LA: yes; SI and TA: no)
                                    </option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-offer-authority"
                            >
                                <Form.Label>Who may make offers</Form.Label>
                                <Form.Select
                                    value={formData.offerAuthority}
                                    onChange={(e) =>
                                        handleChange(
                                            'offerAuthority',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="">
                                        Program default (LA: coordinators or the
                                        section&apos;s faculty; SI and TA:
                                        coordinators)
                                    </option>
                                    <option value="coordinator">
                                        Coordinators only
                                    </option>
                                    <option value="coordinator_or_faculty">
                                        Coordinators or the section&apos;s
                                        faculty
                                    </option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-applicant-visibility"
                            >
                                <Form.Label>Applicant Visibility</Form.Label>
                                <Form.Select
                                    aria-label="Applicant Visibility"
                                    value={formData.visibility}
                                    onChange={(e) =>
                                        handleChange(
                                            'visibility',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="">
                                        Program default (LA: faculty review,
                                        coordinators can step in; SI and TA:
                                        coordinators only)
                                    </option>
                                    <option value="coordinator_only">
                                        Coordinators only
                                    </option>
                                    <option value="coordinator_and_faculty">
                                        Coordinators and faculty
                                    </option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-resume-required"
                            >
                                <Form.Label>
                                    Students must attach a resume to apply
                                </Form.Label>
                                <Form.Select
                                    value={formData.resumeRequired}
                                    onChange={(e) =>
                                        handleChange(
                                            'resumeRequired',
                                            e.target.value
                                        )
                                    }
                                >
                                    <option value="">
                                        {`Program default (${
                                            programType === 'la'
                                                ? 'required'
                                                : 'optional'
                                        })`}
                                    </option>
                                    <option value="true">Required</option>
                                    <option value="false">Optional</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />
                    <h6 className="text-muted mb-3">
                        Student Employment Limits
                    </h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-hours-per-position"
                            >
                                <Form.Label>
                                    Hours per week for one position
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.5"
                                    min="0.5"
                                    max="40"
                                    aria-describedby="admin-hours-per-position-help"
                                    value={formData.hoursPerWeekPerPosition}
                                    onChange={(e) =>
                                        handleChange(
                                            'hoursPerWeekPerPosition',
                                            e.target.value
                                        )
                                    }
                                />
                                <Form.Text id="admin-hours-per-position-help">
                                    How many hours a week one position in this
                                    program takes. Nothing checks it yet: the
                                    check against a student&apos;s weekly limit,
                                    when they accept a position, comes later.
                                    Leave empty to use the program&apos;s
                                    default.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-positions-per-student"
                            >
                                <Form.Label>
                                    Positions one student may hold
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    step="1"
                                    min="1"
                                    max="10"
                                    aria-describedby="admin-positions-per-student-help"
                                    value={formData.maxPositionsPerStudent}
                                    onChange={(e) =>
                                        handleChange(
                                            'maxPositionsPerStudent',
                                            e.target.value
                                        )
                                    }
                                />
                                <Form.Text id="admin-positions-per-student-help">
                                    The most positions in this program one
                                    student should hold at once. Nothing checks
                                    it yet: the check when a student accepts a
                                    position comes later. Leave empty to use the
                                    program&apos;s default.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />
                    <Form.Check
                        type="switch"
                        id="admin-program-active"
                        label="Program is active and accepting applications"
                        checked={formData.isActive}
                        onChange={(e) =>
                            handleChange('isActive', e.target.checked)
                        }
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit">
                        Save Configuration
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

function BudgetModal({ show, onHide, onSave, budget, terms, selectedTerm }) {
    const isEditing = !!budget;

    // A new budget starts with no program: the admin chooses one, so the
    // form favors none and an untouched select cannot save a budget to a
    // program nobody picked.
    const [formData, setFormData] = useState({
        program: '',
        term: '',
        allocatedPositions: '',
        payRate: '',
        hoursPerPosition: '',
        allocatedAmount: '',
        notes: '',
    });
    const [programMissing, setProgramMissing] = useState(false);

    useEffect(() => {
        setProgramMissing(false);
        if (budget?.budget) {
            setFormData({
                program: budget.budget.program ?? '',
                term: budget.budget.term || selectedTerm || '',
                allocatedPositions: budget.budget.allocatedPositions ?? '',
                payRate: budget.budget.payRate ?? '',
                hoursPerPosition: budget.budget.hoursPerPosition ?? '',
                allocatedAmount: budget.budget.allocatedAmount ?? '',
                notes: budget.budget.notes || '',
            });
        } else {
            setFormData({
                program: '',
                term: selectedTerm || '',
                allocatedPositions: '',
                payRate: '',
                hoursPerPosition: '',
                allocatedAmount: '',
                notes: '',
            });
        }
    }, [budget, selectedTerm, show]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (field === 'program' && value) setProgramMissing(false);
    };

    // On an interactive submit the browser blocks an empty Program before
    // handleSubmit runs and fires `invalid` on the select. Cancelling it
    // swaps the native bubble for the inline error the rest of the form
    // uses; the other fields keep native validation (the form is not
    // noValidate, since handleSubmit checks only the program).
    const handleProgramInvalid = (e) => {
        e.preventDefault();
        setProgramMissing(true);
        e.target.focus();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Checked here as well as by the select's `required`: the browser
        // enforces that attribute only on interactive submission, so a
        // programmatic submit would otherwise send an empty program.
        if (!formData.program) {
            setProgramMissing(true);
            return;
        }
        const payload = {
            program: formData.program,
            term: formData.term,
            allocatedPositions: formData.allocatedPositions
                ? parseInt(formData.allocatedPositions, 10)
                : undefined,
            payRate: formData.payRate
                ? parseFloat(formData.payRate)
                : undefined,
            hoursPerPosition: formData.hoursPerPosition
                ? parseInt(formData.hoursPerPosition, 10)
                : undefined,
            allocatedAmount: formData.allocatedAmount
                ? parseFloat(formData.allocatedAmount)
                : undefined,
            notes: formData.notes || '',
        };
        onSave(payload);
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            aria-labelledby="admin-modal-title-3"
        >
            <Modal.Header closeButton>
                <Modal.Title id="admin-modal-title-3">
                    {isEditing ? 'Edit Budget' : 'Create Budget'}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <h6 className="text-muted mb-3">Program &amp; Term</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="admin-program"
                            >
                                <Form.Label>Program</Form.Label>
                                <Form.Select
                                    value={formData.program}
                                    onChange={(e) =>
                                        handleChange('program', e.target.value)
                                    }
                                    disabled={isEditing}
                                    required
                                    onInvalid={handleProgramInvalid}
                                    isInvalid={programMissing}
                                    aria-invalid={programMissing}
                                    aria-describedby={
                                        programMissing
                                            ? 'admin-program-error'
                                            : undefined
                                    }
                                >
                                    <option value="" disabled>
                                        Select a program
                                    </option>
                                    {PROGRAM_DISPLAY_ORDER.map((program) => (
                                        <option key={program} value={program}>
                                            {PROGRAM_CONFIG[program].label} -{' '}
                                            {PROGRAM_CONFIG[program].full}
                                        </option>
                                    ))}
                                </Form.Select>
                                {programMissing && (
                                    <Form.Control.Feedback
                                        type="invalid"
                                        id="admin-program-error"
                                        role="alert"
                                    >
                                        Choose the program this budget is for.
                                    </Form.Control.Feedback>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="admin-term">
                                <Form.Label>Term</Form.Label>
                                <Form.Select
                                    value={formData.term}
                                    onChange={(e) =>
                                        handleChange('term', e.target.value)
                                    }
                                    disabled={isEditing}
                                    required
                                >
                                    <option value="">Select term...</option>
                                    {terms.map((t) => (
                                        <option key={t.code} value={t.code}>
                                            {t.name} ({t.code})
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />
                    <h6 className="text-muted mb-3">Position Allocation</h6>
                    <Row>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="budget-allocated-positions"
                            >
                                <Form.Label>Allocated Positions</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    placeholder="e.g., 15"
                                    value={formData.allocatedPositions}
                                    onChange={(e) =>
                                        handleChange(
                                            'allocatedPositions',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="budget-pay-rate"
                            >
                                <Form.Label>Pay Rate ($/hr)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="e.g., 15.00"
                                    value={formData.payRate}
                                    onChange={(e) =>
                                        handleChange('payRate', e.target.value)
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group
                                className="mb-3"
                                controlId="budget-hours-per-position"
                            >
                                <Form.Label>Hours per Position</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    placeholder="e.g., 160"
                                    value={formData.hoursPerPosition}
                                    onChange={(e) =>
                                        handleChange(
                                            'hoursPerPosition',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />
                    <h6 className="text-muted mb-3">Budget Amount</h6>
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                className="mb-3"
                                controlId="budget-allocated-amount"
                            >
                                <Form.Label>Allocated Amount ($)</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="e.g., 36000"
                                    value={formData.allocatedAmount}
                                    onChange={(e) =>
                                        handleChange(
                                            'allocatedAmount',
                                            e.target.value
                                        )
                                    }
                                    required
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="budget-notes">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Optional notes about this budget allocation..."
                            value={formData.notes}
                            onChange={(e) =>
                                handleChange('notes', e.target.value)
                            }
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-primary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit">
                        {isEditing ? 'Save Changes' : 'Create Budget'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default AdminPage;
